import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import type { CustomDomainStep } from './customDomainPresentation';

interface CustomDomainProgressProps {
  steps: CustomDomainStep[];
}

function StepMarker({ step, position }: { step: CustomDomainStep; position: number }) {
  if (step.state === 'done') {
    return <Icon name="CheckCircle" className="text-lg text-zoosuccesstext" />;
  }
  if (step.state === 'failed') {
    return <Icon name="AlertCircle" className="text-lg text-zooerrortext" />;
  }
  if (step.state === 'inProgress') {
    return <LoadingSpinner className="w-[18px] h-[18px]" />;
  }
  return (
    <span className="w-[18px] h-[18px] rounded-full border border-zooborder text-[10px] font-bold text-zoomuted flex items-center justify-center">
      {position}
    </span>
  );
}

export function CustomDomainProgress({ steps }: CustomDomainProgressProps) {
  const { t } = useTranslation('customDomains');
  return (
    <ol className="space-y-3" aria-label={t('progress.label')}>
      {steps.map(function renderStep(step, index) {
        return (
          <li key={step.key} className="flex items-start gap-3" data-step-state={step.state}>
            <span className="mt-0.5 flex-shrink-0">
              <StepMarker step={step} position={index + 1} />
            </span>
            <div className="min-w-0">
              <p
                className={cn(
                  'text-sm font-semibold',
                  step.state === 'upcoming' ? 'text-zoomuted' : 'text-zooindigo'
                )}
              >
                {t('progress.steps.' + step.key + '.title')}
              </p>
              {step.state === 'inProgress' ? (
                <p className="text-xs text-zoomuted mt-0.5">
                  {t('progress.steps.' + step.key + '.inProgress')}
                </p>
              ) : null}
              {step.state === 'failed' && step.reason !== null ? (
                <div className="mt-1 rounded-xl bg-zooerrorbg p-3">
                  <p className="text-xs font-bold text-zooerrortext">
                    {t('reasons.' + step.reason + '.message')}
                  </p>
                  <p className="text-xs text-zooindigo mt-1">
                    {t('reasons.' + step.reason + '.fix')}
                  </p>
                  {step.key === 'certificate' ? (
                    <p className="text-xs text-zoomuted mt-1">{t('progress.retrying')}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
